import { useEffect, useMemo } from "react";
import { OperationVariables, QueryHookOptions, useQuery } from "react-apollo";
import { DocumentNode } from "graphql";
import {
  isPersistedQueryFresh,
  readPersistedQuery,
  writePersistedQuery,
} from "../helpers/persistedQueryCache";

interface PersistedQueryOptions<TData, TVariables>
  extends QueryHookOptions<TData, TVariables> {
  persistKey: string;
  persistTtl: number;
}

export const usePersistedQuery = <TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  options: PersistedQueryOptions<TData, TVariables>
) => {
  const { persistKey, persistTtl, ...queryOptions } = options;
  const cached = useMemo(() => readPersistedQuery<TData>(persistKey), [
    persistKey,
  ]);
  const fresh = isPersistedQueryFresh(persistKey, persistTtl);
  const skipNetwork = !!cached && fresh;

  const result = useQuery<TData, TVariables>(query, {
    ...queryOptions,
    skip: skipNetwork || queryOptions.skip,
    fetchPolicy: cached ? "cache-and-network" : queryOptions.fetchPolicy,
  });

  useEffect(() => {
    if (result.data) {
      writePersistedQuery(persistKey, result.data);
    }
  }, [persistKey, result.data]);

  return {
    ...result,
    data: result.data ?? cached,
    loading: result.loading && !cached,
  };
};
